import React from "react";
import {
  Table,
  Tag,
  Switch,
  Button,
  Tooltip,
  Avatar,
  Space,
  Typography,
  message,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  CopyOutlined,
  PlayCircleOutlined,
  MailOutlined,
} from "@ant-design/icons";

const { Text } = Typography;

/**
 * Tabla principal del panel admin LiveGames (M13 F2.7).
 *
 * Props:
 *   - items: array de game objects {id, gameKey, label, ...}
 *   - loading: bool
 *   - onEdit(game)
 *   - onDelete(game)
 *   - onToggle(game) - invierte enabled inline
 */
export function LiveGamesTable({
  items,
  loading,
  onEdit,
  onDelete,
  onToggle,
  onSendSdk,
}) {
  const copyGameKey = (gameKey) => {
    navigator.clipboard.writeText(gameKey);
    message.success(`gameKey copiado: ${gameKey}`);
  };

  const columns = [
    {
      title: "Icono",
      dataIndex: "iconUrl",
      key: "icon",
      width: 70,
      render: (url, record) =>
        url ? (
          <Avatar shape="square" size={48} src={url} alt={record.label} />
        ) : (
          <Avatar
            shape="square"
            size={48}
            icon={<PlayCircleOutlined />}
            style={{ backgroundColor: "#1677ff" }}
          />
        ),
    },
    {
      title: "Label",
      dataIndex: "label",
      key: "label",
      render: (label, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{label}</Text>
          {record.description && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.description.length > 80
                ? `${record.description.slice(0, 80)}...`
                : record.description}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: "gameKey",
      dataIndex: "gameKey",
      key: "gameKey",
      render: (gameKey) => (
        <Space>
          <Text code copyable={false}>
            {gameKey}
          </Text>
          <Tooltip title="Copiar gameKey">
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => copyGameKey(gameKey)}
            />
          </Tooltip>
        </Space>
      ),
    },
    {
      title: "Plataformas",
      dataIndex: "platforms",
      key: "platforms",
      render: (platforms = []) => (
        <Space size={[0, 4]} wrap>
          {platforms.map((p) => (
            <Tag key={p} color="geekblue">
              {p}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: "SDK min",
      dataIndex: "sdkMinVersion",
      key: "sdkMinVersion",
      width: 90,
      render: (v) => <Text type="secondary">{v}</Text>,
    },
    {
      title: "Activo",
      dataIndex: "enabled",
      key: "enabled",
      width: 80,
      render: (enabled, record) => (
        <Switch
          checked={enabled}
          onChange={() => onToggle(record)}
          size="small"
        />
      ),
    },
    {
      title: "Acciones",
      key: "actions",
      width: 170,
      render: (_, record) => (
        <Space>
          <Tooltip title="Enviar SDK al dev por email">
            <Button
              type="text"
              icon={<MailOutlined />}
              onClick={() => onSendSdk(record)}
              disabled={!record.enabled}
            />
          </Tooltip>
          <Tooltip title="Editar">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => onEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Eliminar">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => onDelete(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <Table
      rowKey="id"
      dataSource={items}
      columns={columns}
      loading={loading}
      pagination={{ pageSize: 25, showSizeChanger: false }}
      size="middle"
      locale={{ emptyText: "Aun no hay juegos. Pulsa 'Nuevo LiveGame'." }}
    />
  );
}
